import React, { useEffect, useState } from 'react';
import {
  UserOutlined,
  CrownOutlined,
  RiseOutlined,
  DollarOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  HourglassOutlined,
  CalendarOutlined,
  SyncOutlined,
  UserAddOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { Card, Row, Col, Statistic, Table, Tag, Alert, Button, Tooltip } from 'antd';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { fetchAllUsers, fetchAuditLogs } from '../../services/firestoreService';
import { fetchRevenueCatMetrics } from '../../services/revenueCatService';
import { toDate } from '../../utils/timestamp';
import { isActivePremium } from '../../utils/userMapper';
import { debugLog, debugPremiumSnapshot } from '../../utils/debugLog';

const getUserRegisteredAt = (user) =>
  toDate(user.registeredAt || user.createdAt || user.created_at || user.registered_at);

const getUserStatus = (user) =>
  String(user.status || user.accountStatus || 'active').toLowerCase();

const hadPremiumSubscription = (user) =>
  user.subscriptionType === 'premium' ||
  user.type === 'premium' ||
  user.plan === 'premium' ||
  Boolean(user.subscriptionExpiry || user.expiryDate);

const RC_CARDS = [
  { id: 'active_trials', title: 'Active Trials', period: 'In total', icon: <HourglassOutlined className="text-blue-500" />, color: '#3b82f6' },
  { id: 'active_subscriptions', title: 'Active Subscriptions', period: 'In total', icon: <CalendarOutlined className="text-emerald-500" />, color: '#10b981' },
  { id: 'mrr', title: 'MRR', period: 'Monthly Recurring Revenue', icon: <SyncOutlined className="text-green-600" />, color: '#059669', money: true },
  { id: 'revenue', title: 'Revenue', period: 'Last 28 days', icon: <DollarOutlined className="text-green-600" />, color: '#059669', money: true },
  { id: 'new_customers', title: 'New Customers', period: 'Last 28 days', icon: <UserAddOutlined className="text-cyan-500" />, color: '#0891b2' },
  { id: 'active_users', title: 'Active Customers', period: 'Last 28 days', icon: <UserOutlined className="text-purple-500" />, color: '#9333ea' },
];

const AdminDashboard = () => {
  const { logActivity } = useAdminAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    premiumUsers: 0,
    totalRevenue: 0,
    newUsersToday: 0,
    newUsers7Days: 0,
    newUsers30Days: 0,
    suspendedUsers: 0,
    expiredSubscriptions: 0,
    activeSubscriptions: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [rcMetrics, setRcMetrics] = useState(null);
  const [rcLoading, setRcLoading] = useState(false);
  const [rcError, setRcError] = useState(null);

  useEffect(() => {
    logActivity('view_dashboard', 'Viewed admin dashboard');
    loadStatistics();
    loadRecentActivities();
    loadRevenueCatMetrics();
  }, []);

  const loadRevenueCatMetrics = async () => {
    setRcLoading(true);
    setRcError(null);
    try {
      const { metrics } = await fetchRevenueCatMetrics('USD');
      debugLog('Dashboard', 'RevenueCat metrics received', metrics);
      setRcMetrics(metrics);
    } catch (error) {
      debugLog('Dashboard', 'RevenueCat fetch failed', {
        code: error.code,
        message: error.message,
      });
      setRcError(
        error.code === 'not_configured'
          ? error.message
          : error.message || 'Failed to load RevenueCat metrics'
      );
    } finally {
      setRcLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const users = await fetchAllUsers();
      debugPremiumSnapshot('Dashboard', users);
      debugLog('Dashboard', 'active premium count', users.filter((u) => isActivePremium(u)).length);
    
    // Calculate stats
    const totalUsers = users.length;
    const activeUsers = users.filter(u => getUserStatus(u) !== 'suspended' && getUserStatus(u) !== 'deleted').length;
    const premiumUsers = users.filter((u) => isActivePremium(u)).length;
    const suspendedUsers = users.filter(u => getUserStatus(u) === 'suspended').length;
    
    // Calculate new users by time period
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const newUsersToday = users.filter((u) => {
      const userDate = getUserRegisteredAt(u);
      return userDate && userDate >= startOfToday && userDate <= endOfToday;
    }).length;
    
    const newUsers7Days = users.filter((u) => {
      const userDate = getUserRegisteredAt(u);
      return userDate && userDate >= sevenDaysAgo;
    }).length;
    
    const newUsers30Days = users.filter((u) => {
      const userDate = getUserRegisteredAt(u);
      return userDate && userDate >= thirtyDaysAgo;
    }).length;

    const activeSubscriptions = users.filter((u) => isActivePremium(u)).length;

    const expiredSubscriptions = users.filter((u) => {
      if (isActivePremium(u)) return false;
      const expiry = toDate(u.subscriptionExpiry || u.expiryDate);
      if (expiry) return expiry <= now;
      return hadPremiumSubscription(u);
    }).length;

    // Calculate revenue (premium users * $9.99)
    const totalRevenue = activeSubscriptions * 9.99;

    setStats({
      totalUsers,
      activeUsers,
      premiumUsers,
      totalRevenue,
      newUsersToday,
      newUsers7Days,
      newUsers30Days,
      suspendedUsers,
      expiredSubscriptions,
      activeSubscriptions,
    });
    } catch (error) {
      debugLog('Dashboard', 'Failed to load dashboard statistics', error?.message || error);
      console.error('Failed to load dashboard statistics');
    }
  };

  const loadRecentActivities = async () => {
    try {
      const logs = await fetchAuditLogs();
    
    // Get last 10 activities
    const recent = logs.slice(-10).reverse().map((log, index) => ({
      key: index,
      ...log
    }));

    setRecentActivities(recent);
    } catch {
      console.error('Failed to load recent activities');
    }
  };

  const activityColumns = [
    {
      title: 'Admin',
      dataIndex: 'adminName',
      key: 'adminName',
      render: (name) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center text-white text-xs font-bold">
            {name?.charAt(0).toUpperCase()}
          </div>
          <span className="font-medium">{name}</span>
        </div>
      )
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => {
        const colors = {
          login: 'blue',
          logout: 'default',
          view_dashboard: 'cyan',
          update_user: 'orange',
          suspend_user: 'red',
          upgrade_subscription: 'green'
        };
        return <Tag color={colors[action] || 'default'}>{action.replace(/_/g, ' ').toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      ellipsis: true
    },
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => new Date(timestamp).toLocaleString()
    }
  ];

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-500 mt-1">Welcome back! Here's an overview of your platform.</p>
      </div>

      {/* RevenueCat Metrics */}
      <Card
        title="RevenueCat Metrics"
        className="shadow-sm"
        extra={
          <Button
            icon={<ReloadOutlined />}
            size="small"
            loading={rcLoading}
            onClick={loadRevenueCatMetrics}
          >
            Refresh
          </Button>
        }
      >
        {rcError ? (
          <Alert type="warning" showIcon message={rcError} />
        ) : (
          <Row gutter={[16, 16]}>
            {RC_CARDS.map((card) => {
              const metric = rcMetrics?.[card.id];
              return (
                <Col xs={24} sm={12} lg={8} key={card.id}>
                  <Card className="shadow-sm hover:shadow-md transition-shadow" loading={rcLoading}>
                    <Tooltip title={metric?.description}>
                      <Statistic
                        title={`${card.title} (${card.period})`}
                        value={metric ? metric.value : 0}
                        prefix={card.icon}
                        precision={card.money ? 2 : 0}
                        valueStyle={{ color: card.color }}
                        formatter={
                          card.money
                            ? (value) => `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : undefined
                        }
                      />
                    </Tooltip>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="Total Users"
              value={stats.totalUsers}
              prefix={<TeamOutlined className="text-blue-500" />}
              valueStyle={{ color: '#3b82f6' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="Active Users"
              value={stats.activeUsers}
              prefix={<CheckCircleOutlined className="text-green-500" />}
              valueStyle={{ color: '#10b981' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="Premium Users"
              value={stats.premiumUsers}
              prefix={<CrownOutlined className="text-yellow-500" />}
              valueStyle={{ color: '#f59e0b' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="Total Revenue"
              value={stats.totalRevenue}
              prefix={<DollarOutlined className="text-green-600" />}
              precision={2}
              valueStyle={{ color: '#059669' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="New Users Today"
              value={stats.newUsersToday}
              prefix={<RiseOutlined className="text-cyan-500" />}
              valueStyle={{ color: '#0891b2' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="New Users (7 Days)"
              value={stats.newUsers7Days}
              prefix={<RiseOutlined className="text-purple-500" />}
              valueStyle={{ color: '#9333ea' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="New Users (30 Days)"
              value={stats.newUsers30Days}
              prefix={<RiseOutlined className="text-indigo-500" />}
              valueStyle={{ color: '#6366f1' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="Active Subscriptions"
              value={stats.activeSubscriptions}
              prefix={<CheckCircleOutlined className="text-emerald-500" />}
              valueStyle={{ color: '#10b981' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="Expired Subscriptions"
              value={stats.expiredSubscriptions}
              prefix={<CloseCircleOutlined className="text-orange-500" />}
              valueStyle={{ color: '#f97316' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="Suspended Users"
              value={stats.suspendedUsers}
              prefix={<CloseCircleOutlined className="text-red-500" />}
              valueStyle={{ color: '#ef4444' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Recent Activities */}
      <Card title="Recent Admin Activities" className="shadow-sm">
        <Table
          columns={activityColumns}
          dataSource={recentActivities}
          pagination={{ pageSize: 5 }}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
};

export default AdminDashboard;
